import React, { useEffect, useId, useRef, useState } from 'react';

export type PageHeaderOverflowItem = {
  id: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  hidden?: boolean;
};

type Props = {
  items: PageHeaderOverflowItem[];
  label?: string;
  className?: string;
};

/** Collapses secondary header actions into a More menu on narrow viewports. */
export default function PageHeaderOverflowMenu({ items, label = 'More', className }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const visibleItems = items.filter((item) => !item.hidden);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!visibleItems.length) return null;

  return (
    <div ref={rootRef} className={['page-header-overflow', className].filter(Boolean).join(' ')}>
      <button
        type="button"
        className="btn btn-sm btn-ghost page-header-overflow__trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={() => setOpen((value) => !value)}
      >
        {label}
      </button>
      {open ? (
        <div id={menuId} className="page-header-overflow__menu" role="menu">
          {visibleItems.map((item) => (
            <button
              key={item.id}
              type="button"
              role="menuitem"
              className="page-header-overflow__item"
              disabled={item.disabled}
              onClick={() => {
                item.onClick();
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
